/**
 * ContactPanel — S4 #contact
 * canonical: lines 2072–2082
 * Saves: contact_name, contact_phone, contact_email
 */
import { useState, useEffect } from 'react'
import { saveSettings, type CompanySettings } from '../../../api/settings'

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'

interface Props {
  settings: CompanySettings | null
  onSaved: (s: CompanySettings) => void
}

export default function ContactPanel({ settings, onSaved }: Props) {
  const [cName,  setCName]  = useState('')
  const [phone,  setPhone]  = useState('')
  const [email,  setEmail]  = useState('')
  const [status, setStatus] = useState<SaveStatus>('idle')
  const [errMsg, setErrMsg] = useState('')

  useEffect(() => {
    if (!settings) return
    setCName(settings.contact_name  ?? '')
    setPhone(settings.contact_phone ?? '')
    setEmail(settings.contact_email ?? '')
  }, [settings])

  async function handleSave() {
    setStatus('saving')
    try {
      const updated = await saveSettings({
        contact_name:  cName || null,
        contact_phone: phone || null,
        contact_email: email || null,
      })
      onSaved(updated)
      setStatus('saved')
      setTimeout(() => setStatus('idle'), 2500)
    } catch (e) {
      setErrMsg((e as Error).message || 'Save failed')
      setStatus('error')
      setTimeout(() => setStatus('idle'), 4000)
    }
  }

  // Button label never shows raw API error — error appears below as separate text
  const saveLabel = status === 'saving' ? 'Saving…' : status === 'saved' ? 'Saved ✓' : 'Save contact'

  return (
    <div id="contact" className="panel" data-testid="panel-contact" style={{ marginTop: 14 }}>
      <div className="panel-h">
        <div className="panel-tt">Contact information</div>
        <span className="panel-st">Auto-fills new projects</span>
      </div>
      <div className="panel-bp">
        <div className="fld-grid">
          <div className="fld">
            <label className="fld-l">Contact name</label>
            <input className="fld-i" value={cName} onChange={e => setCName(e.target.value)}
              placeholder="Vagish Kapila" data-testid="contact-name-input" />
          </div>
          <div className="fld">
            <label className="fld-l">Contact phone</label>
            <input className="fld-i" value={phone} onChange={e => setPhone(e.target.value)}
              placeholder="+1 (408) 555-1234" data-testid="contact-phone-input" />
          </div>
        </div>
        <div className="fld" style={{ marginTop: 12 }}>
          <label className="fld-l">Contact email</label>
          <input className="fld-i" style={{ width: '100%', boxSizing: 'border-box' }}
            value={email} onChange={e => setEmail(e.target.value)}
            placeholder="office@yourcompany.com" data-testid="contact-email-input" />
        </div>
        <div style={{ marginTop: 14 }}>
          <button
            className={`btn ${status === 'saved' ? 'b-gh' : 'b-dk'}`}
            disabled={status === 'saving'}
            onClick={handleSave}
            data-testid="save-contact-btn"
          >
            {saveLabel}
          </button>
          {status === 'error' && (
            <p style={{ color: 'var(--red)', fontSize: 11, marginTop: 6, marginBottom: 0 }}
              data-testid="save-contact-error">
              {errMsg === 'No token' ? 'Session expired — please refresh the page.' : errMsg || 'Save failed. Try again.'}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
